export type OutlookEventLink = {
  label: string;
  url: string;
};

export type ParsedOutlookEventDescription = {
  notes: string | null;
  meetingUrl: string | null;
  meetingId: string | null;
  passcode: string | null;
  links: OutlookEventLink[];
};

const TEAMS_BOILERPLATE_START = /_{10,}|Microsoft Teams meeting/i;
const JOIN_URL_PATTERN = /Join:\s*(https?:\/\/\S+)/i;
const MEETING_ID_PATTERN = /Meeting ID:\s*(.+)/i;
const PASSCODE_PATTERN = /Passcode:\s*(\S+)/i;
const ANGLE_BRACKET_LINK_PATTERN = /([^<|\n]+?)<(https?:\/\/[^>]+)>/g;

const GENERIC_VIRTUAL_LOCATIONS = new Set(['microsoft teams meeting', 'microsoft teams']);

export function isGenericVirtualLocation(location?: string | null): boolean {
  if (!location) return false;
  return GENERIC_VIRTUAL_LOCATIONS.has(location.trim().toLowerCase());
}

export function parseOutlookEventDescription(
  description: string | undefined | null,
  fallbackMeetingUrl?: string | null
): ParsedOutlookEventDescription {
  if (!description?.trim()) {
    return {
      notes: null,
      meetingUrl: fallbackMeetingUrl ?? null,
      meetingId: null,
      passcode: null,
      links: [],
    };
  }

  const boilerplateIndex = description.search(TEAMS_BOILERPLATE_START);
  const notes = boilerplateIndex === -1 ? description.trim() : description.slice(0, boilerplateIndex).trim();
  const teamsSection = boilerplateIndex === -1 ? '' : description.slice(boilerplateIndex);

  const meetingUrlMatch = teamsSection.match(JOIN_URL_PATTERN);
  const meetingIdMatch = teamsSection.match(MEETING_ID_PATTERN);
  const passcodeMatch = teamsSection.match(PASSCODE_PATTERN);

  const links: OutlookEventLink[] = [];

  for (const linkMatch of teamsSection.matchAll(ANGLE_BRACKET_LINK_PATTERN)) {
    links.push({
      label: linkMatch[1].trim(),
      url: linkMatch[2].trim(),
    });
  }

  return {
    notes: notes || null,
    meetingUrl: meetingUrlMatch?.[1] ?? fallbackMeetingUrl ?? null,
    meetingId: meetingIdMatch?.[1]?.trim() ?? null,
    passcode: passcodeMatch?.[1]?.trim() ?? null,
    links,
  };
}

export function getOutlookEventDisplayNotes(
  description: string | undefined | null,
  locationHint?: string | null
): string | null {
  const { notes } = parseOutlookEventDescription(description);
  if (notes) return notes;
  if (locationHint && !isGenericVirtualLocation(locationHint)) return locationHint.trim();
  return null;
}

export function getOutlookEventSummary(
  description: string | undefined | null,
  locationHint?: string | null,
  maxLength = 120
): string | null {
  const text = getOutlookEventDisplayNotes(description, locationHint);
  if (!text) return null;
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trimEnd()}…`;
}
